import { Injectable } from '@angular/core';
import { RestService } from '@service/rest/rest.service';
import { ToastService } from '@service/toast/toast.service';
import { SerializationService } from 'aautil';
import { Comment } from '@model/comment/comment';
import { imageDefinitions } from '@configuration/image-definitions';
import moment from 'moment';
import { Entity } from '@model/entities/entity';
import { Subject } from 'rxjs';
import { SentryService } from '@service/sentry.service';
import { EntityTypeConfig } from '@model/entity-type-config';
import { EntityTypeConfigService } from '@service/entity-type-config.service';

@Injectable({
  providedIn: 'root',
})
export class CommentService {
  public commentAddedObservable: Subject<boolean> = new Subject<boolean>();
  public commentDeletedObservable: Subject<number> = new Subject<number>();

  constructor(
    private SentryService: SentryService,
    private restService: RestService,
    private toastService: ToastService,
    private entityTypeConfigService: EntityTypeConfigService,
    private SerializationService: SerializationService,
  ) {}

  public getComments(entity: Entity): Promise<Comment[]> {
    return new Promise((resolve, reject) => {
      let entityTypeConfig: EntityTypeConfig = this.entityTypeConfigService.getConfigByEntity(entity);

      const data = {
        model: entityTypeConfig.backendModelName,
        model_id: entity.id,
      };

      // send
      this.restService
        .post<any[]>('comments/getCommentsByModel', data, imageDefinitions.user.small_user_image)
        .then((result) => {
          // deserialize
          this.SerializationService.deserializeMany(Comment, result)
            .then((serialized_comments) => {
              if (serialized_comments.length > 0) {
                const sorted = serialized_comments.sort((a, b) => {
                  if (a.count_likes < b.count_likes) {
                    return 1;
                  }
                  if (a.count_likes > b.count_likes) {
                    return -1;
                  }

                  const aMoment = moment(a.created_at);
                  const bMoment = moment(b.created_at);
                  if (aMoment.isBefore(bMoment)) {
                    return 1;
                  }
                  if (aMoment.isAfter(bMoment)) {
                    return -1;
                  }
                  return 0;
                });
                resolve(sorted);
              } else {
                resolve(serialized_comments);
              }
            })
            .catch((err) => {
              this.SentryService.silentCaptureException(err);
              this.toastService.showError('Fehler beim Laden der Kommentare, bitte versuchen Sie es erneut.');
              reject(err);
            });
        })
        .catch((err) => {
          this.toastService.showError('Fehler beim Laden der Kommentare, bitte versuchen Sie es erneut.');
          reject(err);
        });
    });
  }

  public addComment(entity: Entity, message: string): Promise<Comment> {
    return new Promise((resolve, reject) => {
      const data = {
        model: 'Chat',
        model_id: entity.id,
        message,
      };

      // send
      this.restService
        .post(
          entity.entityTypeIdentifier.toLowerCase() + '/createComment',
          data,
          imageDefinitions.user.small_user_image,
        )
        .then((response: any) => {
          // deserialize
          this.SerializationService.deserialize(Comment, response)
            .then((comment: Comment) => {
              this.commentAddedObservable.next(true);
              resolve(comment);
            })
            .catch((err) => {
              this.SentryService.silentCaptureException(err);
              reject();
            });
        })
        .catch((err) => {
          this.SentryService.silentCaptureException(err);
          reject(err);
        });
    });
  }

  public deleteComment(id: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const data = {
        comment_id: id,
      };

      // send
      this.restService
        .post('comments/deleteComment', data)
        .then(() => {
          this.commentDeletedObservable.next(id);
          resolve();
        })
        .catch((err) => reject(err));
    });
  }
}
